import React, { useCallback, useEffect, useMemo, useState } from 'react';
import bridgeService from '../../../services/bridgeService';
import Loading from '../../../components/common/Loading';
import BridgeMapPanel, { getBridgeCoordinates } from '../../../components/bridge/BridgeMapPanel';
import BridgeDashboardView from '../../dashboard/components/BridgeDashboardView';
import SensorDataPage from '../../sensor-data/pages/SensorDataPage';
import useNotify from '../../../hooks/useNotify';
import { filterBridgesByAdmin } from '../../../utils/bridgeAccess';
import { getRespondedAdminName } from '../../../components/notifications/NotificationBell';
import '../../../modules/bridges/pages/BridgeListPage.css';
import './CompanyAdminBridgePanel.css';

const mergeBridgeListSources = (listBridges, mapBridges) => {
  const merged = new Map();

  const add = (bridge) => {
    if (!bridge?._id) return;
    const key = String(bridge._id);
    const existing = merged.get(key);
    if (!existing) {
      merged.set(key, { ...bridge });
      return;
    }
    merged.set(key, {
      ...existing,
      ...bridge,
      createdBy: bridge.createdBy ?? existing.createdBy,
      admins: bridge.admins?.length ? bridge.admins : existing.admins,
      owners: bridge.owners?.length ? bridge.owners : existing.owners,
      managers: bridge.managers?.length ? bridge.managers : existing.managers,
      company: bridge.company ?? existing.company,
    });
  };

  (mapBridges || []).forEach(add);
  (listBridges || []).forEach(add);
  return Array.from(merged.values());
};

export default function CompanyAdminBridgePanel({ company, accessRequest }) {
  const { showToast } = useNotify();
  const [bridges, setBridges] = useState([]);
  const [mapBridges, setMapBridges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [mapBridge, setMapBridge] = useState(null);
  const [expandedBridgeId, setExpandedBridgeId] = useState(null);

  const respondedBy = accessRequest?.respondedBy;

  const adminSeeds = useMemo(() => {
    const seeds = [];
    if (respondedBy) seeds.push(respondedBy);
    (company?.bridgeAdmins || []).forEach((admin) => {
      seeds.push({ _id: admin.user, email: admin.email });
    });
    return seeds;
  }, [respondedBy, company]);

  const loadBridges = useCallback(async () => {
    setLoading(true);
    try {
      const [listData, mapData] = await Promise.all([
        bridgeService.getAll(),
        bridgeService.getAllForMap(),
      ]);
      const listBridges = listData.bridges || [];
      const mapBridgeList = mapData.bridges || [];
      const merged = mergeBridgeListSources(listBridges, mapBridgeList);
      const adminBridges = filterBridgesByAdmin(merged, adminSeeds, company._id);
      setBridges(adminBridges);
      setMapBridges(
        mapBridgeList.filter((b) => adminBridges.some((ab) => String(ab._id) === String(b._id)))
      );
    } catch (e) {
      showToast(e.message, 'error');
      setBridges([]);
      setMapBridges([]);
    } finally {
      setLoading(false);
    }
  }, [company._id, adminSeeds, showToast]);

  useEffect(() => {
    loadBridges();
  }, [loadBridges]);

  const filtered = useMemo(
    () =>
      bridges.filter((b) =>
        [b.name, b.country, b.state, b.city, b.division]
          .join(' ')
          .toLowerCase()
          .includes(search.toLowerCase())
      ),
    [bridges, search]
  );

  const assignedBridgeIds = useMemo(() => bridges.map((b) => b._id), [bridges]);

  const handleAssignedBridgeMapClick = useCallback((bridge) => {
    setExpandedBridgeId(bridge._id);
    if (getBridgeCoordinates(bridge)) {
      setMapBridge(bridge);
    }
  }, []);

  const scrollToSensorData = useCallback((bridgeId) => {
    window.requestAnimationFrame(() => {
      document.getElementById(`company-sensor-data-${bridgeId}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  }, []);

  const handleBridgeNameClick = (bridge) => {
    const isOpen = String(expandedBridgeId) === String(bridge._id);
    setExpandedBridgeId(isOpen ? null : bridge._id);
    if (getBridgeCoordinates(bridge)) {
      setMapBridge(bridge);
    }
  };

  const adminName = getRespondedAdminName(accessRequest) || 'Bridge admin';

  const mapPanel = (
    <div className="card bridge-list-map-panel">
      <BridgeMapPanel
        bridges={mapBridges.length ? mapBridges : bridges}
        assignedBridgeIds={assignedBridgeIds}
        selectedBridge={mapBridge}
        onAssignedBridgeClick={handleAssignedBridgeMapClick}
        onLoadError={(message) => showToast(message, 'error')}
      />
    </div>
  );

  return (
    <div className="company-admin-bridge-panel">
      <div className="company-admin-bridge-panel__header">
        <span>
          Bridges under <strong>{adminName}</strong>
        </span>
      </div>

      {loading ? (
        <Loading message="Loading bridges..." />
      ) : (
        <>
        <div className={`bridge-list-layout${expandedBridgeId ? '' : ' bridge-list-layout--split'}`}>
          <div className="card bridge-list-table-panel">
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
              <i className="ti ti-barrier-block" style={{ fontSize: 22, color: '#1a6fa8', marginRight: 8 }} />
              <span style={{ fontWeight: 700, fontSize: 15, color: '#1a6fa8' }}>Added Bridges</span>
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
                <i className="ti ti-search" style={{ color: '#888' }} />
                <input
                  type="text"
                  placeholder="Search bridges..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ width: 200 }}
                />
              </div>
            </div>

            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Bridge Name</th>
                  <th>Country</th>
                  <th>State</th>
                  <th>City</th>
                  <th>Division</th>
                  <th>ZIP Code</th>
                  <th>Sensors</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan="8" style={{ textAlign: 'center', padding: 20, color: '#aaa' }}>
                      No bridges found for this admin.
                    </td>
                  </tr>
                ) : (
                  filtered.map((b, idx) => {
                    const isDashboardOpen = String(expandedBridgeId) === String(b._id);
                    return (
                    <React.Fragment key={b._id}>
                    <tr
                      className={isDashboardOpen ? 'bridge-list-row--selected' : undefined}
                    >
                      <td>{idx + 1}</td>
                      <td>
                        <button
                          type="button"
                          className={`bridge-name-link${isDashboardOpen ? ' bridge-name-link--active' : ''}`}
                          onClick={() => handleBridgeNameClick(b)}
                          title="Show bridge dashboard"
                        >
                          {b.name}
                          <i
                            className={`ti ti-chevron-${isDashboardOpen ? 'up' : 'down'}`}
                            style={{ marginLeft: 6, fontSize: 11 }}
                          />
                        </button>
                      </td>
                      <td>{b.country}</td>
                      <td>{b.state}</td>
                      <td>{b.city}</td>
                      <td>{b.division}</td>
                      <td>{b.zipCode}</td>
                      <td>
                        <span
                          style={{
                            background: '#eef4fb',
                            padding: '2px 8px',
                            borderRadius: 10,
                            fontSize: 11,
                            color: '#1a6fa8',
                          }}
                        >
                          {b.sensors?.length || 0} sensors
                        </span>
                      </td>
                    </tr>
                    {isDashboardOpen && (
                      <tr className="bridge-dashboard-expanded">
                        <td colSpan={8}>
                          <BridgeDashboardView
                            bridgeId={b._id}
                            embedded
                            mapPanel={mapPanel}
                            onGoToSensorData={() => scrollToSensorData(b._id)}
                          />
                          <div
                            id={`company-sensor-data-${b._id}`}
                            className="company-sensor-data-section"
                          >
                            <SensorDataPage
                              embedded
                              fixedBridges={bridges}
                              externalBridgeId={b._id}
                              hideBridgeDetails
                            />
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {!expandedBridgeId && mapPanel}
        </div>
        </>
      )}
    </div>
  );
}
